import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import MaterialTable from "@material-table/core";
import { TablePagination, useTheme } from "@material-ui/core";

import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import CurrencyLookup from "../../Currencies/components/CurrencyLookup";
import {
  MaskedText,
  DatePicker, MaskedNumber,
} from "../../../controls";
import AircraftRatesValidator from "../validators/AircraftRatesValidator";
import { useAircraftRates, useAddAircraftRate, useUpdateAircraftRate, useDeleteAircraftRate } from "../../../services/aircraft";
import AircraftHourPoolLookup from "./AircraftHourPoolLookup";
import AircraftInHourPool from "./AircraftInHourPool";
import AircraftBlendedRate from "./AircraftBlendedRate";
const AccessControl = require("../../../AccessControl");

const dateFormat = "DD/MM/YYYY";
const today = moment().toISOString();


const getUserName = () => {
  const userInfo = AccessControl.getUserInfo();
  if (userInfo && "firstName" in userInfo && "lastName" in userInfo)
    return (userInfo.firstName + " " + userInfo.lastName);
  else
    return "";
}

const AircraftRateList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const validatorColumn = ValidatorColumn(AircraftRatesValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    currentUser: getUserName()
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddAircraftRate = useAddAircraftRate();
  const handleUpdateAircraftRate = useUpdateAircraftRate();
  const handleDeleteAircraftRate = useDeleteAircraftRate();

  const queryClient = useQueryClient();
  const extraKeys = [props.aircraftUuid];
  const aircraftRates = useAircraftRates({tableRef, queryClient, extraKeys});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "ACMI Rate",
        field: "acmiRate",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.acmiRate}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(150)),
        title: "ACMI Sell Rate",
        field: "acmiSellRate",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.acmiSellRate}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...ColumnWidth(25),
        ...baseColumn,
      },
      {
        ...baseColumn,
        ...(ColumnWidth(150)),
        title: "Currency Code",
        field: "currencyCode",
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <CurrencyLookup
            showChildCurrencies={true}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right"}}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(240)),
        title: "Hours Pool",
        field: "hourPoolUuid",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <AircraftHourPoolLookup
            returnType={"object"}
            value={props.value}
            onChange={(returnObject) => {
              if (returnObject && returnObject.found) {
                const pool = returnObject.data
                const data = {
                  ...props.rowData,
                  hourPoolUuid: pool.aircraftHoursPoolUuid,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
        render: (rowData) => {return (
          <AircraftHourPoolLookup
            render={true}
            returnType={"object"}
            value={rowData.hourPoolUuid}
          />
        )}
      },
      {
        ...ColumnWidth(425),
        ...baseColumn,
        title: "Aircraft in Hour Pool",
        field: "hourPoolUuid",
        editable: "never",
        render: (rowData) => (
          <AircraftInHourPool
            value={rowData.hourPoolUuid}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(180)),
        title: "Blended ACMI Rate",
        field: "acmiRate",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editable: "never",
        render: (rowData) => (
          <AircraftBlendedRate
            poolUuid={rowData.hourPoolUuid}
            acmiRate={rowData.acmiRate}
            validFrom={rowData.validFrom}
          />
        )
      },
      {
        ...ColumnWidth(25),
        ...baseColumn,
      },
      {
        ...baseColumn,
        title: "Valid From",
        field: "validFrom",
        type: "date",
        emptyValue: today,
        initialEditValue:today,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
            <DatePicker
              // minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        )
      },
      {
        ...baseColumn,
        title: "Entered By",
        field: "enteredBy",
        initialEditValue: state.currentUser,
        editable: "never",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(40)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
    ];
  };

  return (isAuthenticated &&
      <div style={{marginLeft: 50}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              backgroundColor: "#fdfdfd",
              border: "2px solid #aaaaaa",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 5,
              detailPanelType: "single",
              addRowPosition: "first",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Aircraft Rates"
            columns={getColumns({canEdit: state.canEdit})}
            data={aircraftRates.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddAircraftRate.mutate({record: {...newData, aircraftUuid: props.aircraftUuid}, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateAircraftRate.mutate({record: {...newData, aircraftUuid: props.aircraftUuid}, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteAircraftRate.mutate({oldRecord: {...oldData, aircraftUuid: props.aircraftUuid}, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default AircraftRateList;
